import classNames from 'classnames';
import { type ComponentProps, memo, type ReactNode } from 'react';

import { ChevronDownIcon } from '@air/design-icons';

export type TreeHeaderProps = ComponentProps<'div'> & {
  adornmentLeft?: ReactNode;
  adornmentRight?: ReactNode;
  isExpanded?: boolean;
  onTitleClick: ComponentProps<'button'>['onClick'];
  showAdornmentLeft?: boolean;
  showAdornmentRight?: boolean;
  title: ReactNode;
};

export const TreeHeader = memo(
  ({
    adornmentLeft,
    adornmentRight,
    className,
    isExpanded = false,
    onTitleClick,
    showAdornmentLeft,
    showAdornmentRight,
    title,
    ...restOfProps
  }: TreeHeaderProps) => {
    return (
      <header
        className={classNames('group flex h-9 items-center gap-2', className)}
        // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
        data-testid={`TREE_HEADER-${title}`}
        {...restOfProps}
      >
        {adornmentLeft && (
          <div
            className={classNames(
              'flex shrink-0 gap-1',
              showAdornmentLeft ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            {adornmentLeft}
          </div>
        )}
        {title && (
          <button
            className="mr-auto flex h-6 select-none items-center gap-1 rounded-4 border-0 bg-transparent px-2 font-semibold text-12 text-gray-9 transition-colors hover:bg-gray-4"
            onClick={onTitleClick}
            type="button"
          >
            {title}
            <ChevronDownIcon
              className={classNames('block transition-transform', isExpanded ? 'rotate-0' : '-rotate-90')}
              size="sm"
            />
          </button>
        )}

        {adornmentRight && (
          <div
            className={classNames(
              'flex shrink-0 gap-1',
              showAdornmentRight ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            {adornmentRight}
          </div>
        )}
      </header>
    );
  },
);

TreeHeader.displayName = 'TreeHeader';
